import { useEffect, useRef, useCallback } from 'react';
import { ScenarioArtifact, ArtifactSnapshot } from '../types/simulation.types';

export function useArtifactTracking(artifacts: ScenarioArtifact[]) {
  const beforeRef = useRef<Map<string, unknown>>(new Map());
  const readSet = useRef<Set<string>>(new Set());
  const writeSet = useRef<Set<string>>(new Set());

  // Capture baseline snapshot when artifacts change (mount, mutation injection).
  // Do NOT clear readSet/writeSet here — that's only done by resetTracking().
  // Clearing here would erase reads tracked by ArtifactRenderer children,
  // because React runs children effects before parent effects.
  useEffect(() => {
    beforeRef.current = new Map(
      artifacts.map((a) => [a.artifactId, structuredClone(a.content)]),
    );
  }, [artifacts]);

  const trackRead = useCallback((artifactId: string) => {
    readSet.current.add(artifactId);
  }, []);

  const trackWrite = useCallback((artifactId: string) => {
    writeSet.current.add(artifactId);
  }, []);

  const getSnapshots = useCallback((): {
    artifactsBefore: ArtifactSnapshot[];
    artifactsAfter: ArtifactSnapshot[];
    artifactReadCount: number;
    artifactWriteCount: number;
  } => {
    return {
      artifactsBefore: artifacts.map((a) => ({
        artifactId: a.artifactId,
        content: beforeRef.current.get(a.artifactId) ?? a.content,
      })),
      artifactsAfter: artifacts.map((a) => ({
        artifactId: a.artifactId,
        content: a.content,
      })),
      artifactReadCount: readSet.current.size,
      artifactWriteCount: writeSet.current.size,
    };
  }, [artifacts]);

  // Reset tracking after turn submission (call after getSnapshots).
  // Clears write set but pre-seeds reads for all visible artifacts since
  // the workspace stays mounted — ArtifactRenderer won't re-mount next turn.
  const resetTracking = useCallback(() => {
    beforeRef.current = new Map(
      artifacts.map((a) => [a.artifactId, structuredClone(a.content)]),
    );
    readSet.current.clear();
    writeSet.current.clear();
    // Pre-seed reads: artifacts are still visible to the candidate
    artifacts.forEach((a) => readSet.current.add(a.artifactId));
  }, [artifacts]);

  return { trackRead, trackWrite, getSnapshots, resetTracking };
}
